import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { localize } from '@saviynt/common';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import useOutsideClickEffect from '../../../hooks/use-outsideclickeffect';
import InputField from '../../Input/InputField/InputField';
import Loader from '../../Loader/Loader';
import MenuItem from '../MenuItem/MenuItem';

import './MenuInput.css';

/**
 * - MenuInput renders a dropdown which can be typed into to filter options.
 * - Parent should define the width and not have position:static.
 * #### WIP Todo (CPAM):
 * - Finish styling.
 * - Expand menu up/down logic.
 */

const msgs = {
  neo: {
    common: {
      inputPlaceholder: {
        id: 'neo.common.inputPlaceholder',
        defaultMessage: 'Type to search',
      },
      loadingText: {
        id: 'neo.common.loadingText',
        defaultMessage: 'Loading...',
      },
      noDataAvailable: {
        id: 'neo.common.noDataAvailable',
        defaultMessage: 'No data available',
      },
    },
  },
};

const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  XLARGE: 'xLarge',
};

function MenuInput({
  options,
  onChange,
  searchTerm,
  setSearchTerm,
  selectedOptions,
  setSelectedOptions,
  backgroundColor,
  CriticalHelperText,
  isCritical,
  isReadOnly,
  className,
  placeholder,
  prefixIcon,
  dataTestId,
  isMulti,
  suffixIcon,
  isLoading,
  noDataText,
  dropdownClassName,
  onScroll,
  hasMoreOptions,
  inputFieldTestId,
  loadingText,
  isCloseOnSelect,
  size,
  heightConfigs,
  onSearchTermChange,
  menuItemHeightConfigs,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState(searchTerm); // for input search.

  const intl = useIntl();
  const PLACEHOLDER = localize(intl, msgs.neo.common.inputPlaceholder);
  const LOADING_TEXT = localize(intl, msgs.neo.common.loadingText);
  const NO_DATA_AVAILABLE_TEXT = localize(
    intl,
    msgs.neo.common.noDataAvailable
  );

  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  useOutsideClickEffect(wrapperRef, () => setIsOpen(false), isOpen);

  const selectedElement = options.find((option) => option.isChecked) || null;

  useEffect(() => {
    if (!isMulti && selectedElement) {
      setSearchText(selectedElement.label);
    }
  }, [selectedElement]);

  useEffect(() => {
    setSearchTerm(searchText);
  }, [searchText, setSearchTerm]);

  // to clear the selection
  const clearSelection = () => {
    onChange({});
  };

  const handleOptionClick = (option) => {
    if (isMulti) {
      // Here we are trying to see if the selected option already exists in the list then remove it, else add it with isChecked as true
      const updatedOptions = selectedOptions?.some(
        (selected) => selected.value === option.value && selected.isChecked
      )
        ? selectedOptions?.filter((selected) => selected.value !== option.value)
        : [...selectedOptions, { ...option, isChecked: true }];

      setSelectedOptions(updatedOptions);
    }

    onChange(option);
    setIsOpen(isMulti && isCloseOnSelect);
  };

  const handleBlur = () => {
    const matchingOption = options.find(
      (option) => option?.label?.toLowerCase() === searchText?.toLowerCase()
    );

    if (!matchingOption) {
      setSearchText(selectedElement.label);
    }
  };

  const handleSelectFirstOption = () => {
    const matchingOption = options.find((option) =>
      option?.label?.toLowerCase().startsWith(searchText?.toLowerCase())
    );

    if (isOpen && matchingOption) {
      handleOptionClick(matchingOption);
    }
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
        setIsOpen(true); // open dropdown
        break;
      case 'Escape':
        setIsOpen(false); // close dropdown
        break;
      case 'Tab':
        handleBlur(); // ensure menu input value is valid
        break;
      case 'Enter':
        handleSelectFirstOption(); // select first option
        break;
      default:
        break;
    }
  };

  const renderTrigger = () => (
    <InputField
      name='MenuInput-InputField'
      placeholder={placeholder || PLACEHOLDER}
      onClick={() => setIsOpen(!isOpen)}
      suffixButtonType='dropdown'
      suffixIsToggle={isOpen}
      suffixOnClick={() => setIsOpen(!isOpen)}
      value={searchText}
      setValue={setSearchText}
      prefixIcon={prefixIcon}
      onChange={(e) => {
        const newValue = e.target.value;

        setSearchText(newValue);
        onSearchTermChange(newValue);
      }}
      onKeyDown={handleKeyDown}
      backgroundColor={backgroundColor}
      isCritical={isCritical}
      CriticalHelperText={CriticalHelperText}
      isReadOnly={isReadOnly}
      isOpen={isOpen}
      resetValue={clearSelection}
      dataTestId={inputFieldTestId}
    />
  );

  const renderLoaderAndLoadingText = () => (
    <div className='MenuInput--isLoading'>
      <Loader kind='loop' format='inline' color='info' />
      {loadingText || LOADING_TEXT}
    </div>
  );

  const renderMenuItems = () => {
    const filteredOptions = options
      .map((option) => ({
        ...option,
        isChecked: selectedOptions?.some(
          (selected) => selected.value === option.value && selected.isChecked
        ),
      }))
      .filter((option) =>
        option?.label?.toLowerCase().includes(searchText?.toLowerCase())
      );

    if (!isMulti) {
      const showOptions =
        isOpen && searchText !== selectedElement?.label
          ? filteredOptions
          : options;

      return showOptions.map((option) => (
        <React.Fragment key={option.value}>
          <MenuItem
            key={option.value}
            option={option}
            onClick={() => !option.isDisabled && handleOptionClick(option)}
            isDisabled={option.isDisabled}
            tabIndex={isOpen ? 0 : -1}
          />
        </React.Fragment>
      ));
    }

    if (filteredOptions.length === 0) {
      return isLoading ? (
        renderLoaderAndLoadingText()
      ) : (
        <div className='MenuInput--hasNoData'>
          {noDataText || NO_DATA_AVAILABLE_TEXT}
        </div>
      );
    }

    return (
      <>
        {filteredOptions.map((option) => (
          <MenuItem
            key={option.value}
            option={option}
            onClick={() => !option.isDisabled && handleOptionClick(option)}
            isDisabled={option.isDisabled}
            tabIndex={isOpen ? 0 : -1}
            isMulti={isMulti}
            suffixIcon={suffixIcon}
            menuItemHeightConfigs={menuItemHeightConfigs}
          />
        ))}
        {isLoading && hasMoreOptions && renderLoaderAndLoadingText()}
      </>
    );
  };

  const inlineHeightStyle = heightConfigs && {
    minHeight: heightConfigs.minHeight,
    height: heightConfigs.height,
    maxHeight: heightConfigs.maxHeight,
  };

  return (
    <div
      className={classnames(
        'MenuInput',
        suffixIcon && 'MenuInput--hasSuffixIcon',
        className
      )}
      data-testid={dataTestId}
      ref={wrapperRef}
      role='combobox'
      aria-controls='custom-select-listbox'
      aria-expanded={isOpen}>
      {renderTrigger()}
      <div
        className={classnames(
          'Menu-dropdown',
          isOpen && 'Menu-dropdown--isVisible',
          size && `Menu-dropdown--${size}`,
          dropdownClassName
        )}
        onScroll={onScroll}
        ref={dropdownRef}
        style={inlineHeightStyle}>
        <ul className='Menu-items' role='listbox' tabIndex='-1'>
          {renderMenuItems()}
        </ul>
      </div>
    </div>
  );
}

MenuInput.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      isDisabled: PropTypes.bool,
      isChecked: PropTypes.bool,
    })
  ).isRequired,
  searchTerm: PropTypes.string,
  setSearchTerm: PropTypes.func,
  selectedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      isChecked: PropTypes.bool.isRequired,
    })
  ),
  setSelectedOptions: PropTypes.func,
  placeholder: PropTypes.string,
  prefixIcon: PropTypes.element,
  isMulti: PropTypes.bool,
  suffixIcon: PropTypes.element,
  isLoading: PropTypes.bool,
  noDataText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  backgroundColor: PropTypes.string,
  CriticalHelperText: PropTypes.node,
  isCritical: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  className: PropTypes.string,
  dropdownClassName: PropTypes.string,
  dataTestId: PropTypes.string,
  onScroll: PropTypes.func,
  hasMoreOptions: PropTypes.bool,
  inputFieldTestId: PropTypes.string,
  loadingText: PropTypes.string,
  isCloseOnSelect: PropTypes.bool,
  size: PropTypes.oneOf(
    null,
    SIZES.SMALL,
    SIZES.MEDIUM,
    SIZES.LARGE,
    SIZES.XLARGE
  ),
  heightConfigs: PropTypes.shape({
    minHeight: PropTypes.string || null,
    height: PropTypes.string || null,
    maxHeight: PropTypes.string || null,
  }),
  menuItemHeightConfigs: PropTypes.shape({
    minHeight: PropTypes.string || null,
    height: PropTypes.string || null,
    maxHeight: PropTypes.string || null,
  }),
  onSearchTermChange: PropTypes.func,
};

MenuInput.defaultProps = {
  backgroundColor: 'neutral',
  CriticalHelperText: null,
  isCritical: false,
  placeholder: '',
  prefixIcon: null,
  isMulti: false,
  suffixIcon: null,
  isLoading: false,
  selectedOptions: null,
  setSelectedOptions: null,
  noDataText: '',
  isReadOnly: false,
  className: '',
  dropdownClassName: '',
  dataTestId: '',
  onScroll: null,
  hasMoreOptions: false,
  inputFieldTestId: '',
  loadingText: '',
  searchTerm: '',
  setSearchTerm: () => {},
  isCloseOnSelect: false,
  size: null, // default for backwards compatibility
  heightConfigs: {
    minHeight: null,
    height: null,
    maxHeight: null,
  },
  menuItemHeightConfigs: {
    minHeight: null,
    height: null,
    maxHeight: null,
  },
  onSearchTermChange: () => {},
};

export default MenuInput;
